import { eraroCodesMap, createEraroError } from '@dazn/bootstrap-lib';
import {
    createStorageModule,
    createMainModules,
    getBrowserLanguages,
    chapterNames,
    environments,
    handleCommonFeEvent,
} from '@dazn/bootstrap-main';
import type { ConfigurationModuleParams } from '@dazn/bootstrap-main';

import { version } from '../package.json';

import { getDeepLink } from './getDeepLink';

const FEATUREVISOR_DATAFILE_URL = {
    [environments.DEVELOPMENT]:
        'https://features.fe.indazn.com/staging/datafile-tag-web.json',
    [environments.TESTING]:
        'https://features.fe.indazn.com/staging/datafile-tag-web.json',
    [environments.EDGE]:
        'https://features.fe.indazn.com/staging/datafile-tag-web.json',
    [environments.STAGING]:
        'https://features.fe.indazn.com/staging/datafile-tag-web.json',
    [environments.PRODUCTION]:
        'https://features.fe.indazn.com/production/datafile-tag-web.json',
};

export function launchBootstrap({
    window,
    config: configurationJSON,
}: {
    window: Window & typeof globalThis;
    config: ConfigurationModuleParams;
}) {
    const asyncStorage = createStorageModule(window);

    const {
        nrTrace,
        module,
        initMainModules,
        handleInitError,
        loadInitialChapter,
    } = createMainModules(window, {
        asyncStorage,
        __HACK__languageStorage: false,
        launchURL: window.location.href,
        applicationType: 'web.hybrid.2',
        authChapterName: chapterNames.SIGN_IN,
        configurationJSON,
        devModeIndicatorVariant: 'none',
        manufacturer: '',
        targetVersion: version,
        maxSplashScreenVisibilityTime: null,
        selectorsExcludedFromCleaning: [],
        splashScreenBehaviour: 'remove',
        onReady: () => Promise.resolve({ model: 'N/A' }),
        exitApp: () => window.close(),
        restartApp: () => window.location.reload(),
        getLanguages: getBrowserLanguages,
        handleFeEvent: handleCommonFeEvent,
        getPlayerTarget: () => null,
        fetchDeviceId: null,
        fetchPartnerAccessCode: null,
        getAdvertisingDeviceId: null,
        closedCaptionsAPI: null,
        textToSpeechAPI: null,
        featurevisorDatafileURL:
            FEATUREVISOR_DATAFILE_URL[configurationJSON.environment],
    });

    return Promise.resolve()
        .then(initMainModules)
        .then(() => {
            const isBrowserCheck = nrTrace.wrapAsyncFn('browserCheck', () => {
                const isBrowserDeprecated = module.fflag.isEnabledAnonymous(
                    'f_bootstrap_browser_deprecation_enabled',
                );
                if (isBrowserDeprecated)
                    throw createEraroError(
                        'Unsupported browser',
                        eraroCodesMap.category.UnsupportedBrowser,
                        eraroCodesMap.code.GenericError,
                        eraroCodesMap.internal.GenericError,
                    );
            });

            const exposePromoIdFromLocalStorage = nrTrace.wrapAsyncFn(
                'exposePromoIdFromLocalStorage',
                () => {
                    const promoIdKey = 'promoCode';
                    return asyncStorage
                        .getItem(promoIdKey)
                        .then(promoIdValue => {
                            if (promoIdValue) {
                                module.startup.setPromoId(promoIdValue);
                                asyncStorage.removeItem(promoIdKey);
                            }
                        });
                },
            );
            return Promise.all([
                isBrowserCheck(),
                exposePromoIdFromLocalStorage(),
            ]);
        })
        .then(() => {
            const deepLink = getDeepLink(window);

            if (deepLink?.chapterName) {
                window.history.pushState(
                    {},
                    document.title,
                    `/${module.configuration.chapters.homePage(
                        deepLink.chapterName,
                    )}`,
                );
            }
            return loadInitialChapter(deepLink);
        })
        .catch(handleInitError);
}
